import { BalanceData, Rarity } from "../data/balance";
import { DailyDealsDef, OfferDef, OfferItem, OfferPageDef, OfferPrice, OffersData, Rotation } from "../data/offers";
import { Fix64 } from "../maths/fix64";
import { Pcg32 } from "../maths/pcg32";
import { ChestResult } from "../sim/chest";
import { GearItem, generateGear } from "./gear";
import { hasRandomItem } from "./offerValue";
import { SECONDS_PER_DAY, chestOfRarity, chestShare, metaSeed, utcDay, utcWeek } from "./orders";
import { PlayerProfile } from "./profile";
import { Wallet } from "./wallet";

/**
 * The Deals screen's rules (docs/SHOP.md §1, §4): real rotation periods (UTC day, UTC week from Monday, every second
 * UTC week, once per save), the day's Daily Deals theme, what an offer holds as shown (its fixed items, the picks and
 * prize the player chose, the day's theme) and paying those items into the profile and wallet. Pure and
 * deterministic: every chest and gear roll is seeded from (player, the purchase's reference). The offer state (what
 * was bought this period, picks, tokens) is meta/dealsState.ts; the real-money path is @highmarch/app deals.ts.
 */

const DAYS_PER_WEEK = 7;
/** utcWeek counts from a Monday three days before day 0 (orders.ts): week w starts on day 7w − 3. */
const WEEK_START_SHIFT_DAYS = 3;
const WEEKS_PER_FORTNIGHT = 2;
const PERCENT = 100;
/** Pcg32 streams, apart from every other use of the same seeds. */
const THEME_STREAM = 401;
const LUCKY_STREAM = 409;
const GEAR_STREAM = 419;

/** Daily Deals as offers: tiers 1..3 and Buy All (their items depend on the day's theme). */
export const DAILY_PAGE = "daily";
export const DAILY_OFFER_IDS = ["daily.1", "daily.2", "daily.3"] as const;
export const DAILY_BUY_ALL = "daily.all";
/** The ref prefix of a free Weekly Deal milestone (`track:<week>:<tokens>`): its gems are logged as `dealTrack`. */
export const TRACK_REF = "track:";

/** A rotation period: its key (the UTC day, week or fortnight number; 0 for once/none) and when it ends (null: never). */
export interface Period { readonly key: number; readonly endsAt: number | null }

const weekStart = (week: number): number => (week * DAYS_PER_WEEK - WEEK_START_SHIFT_DAYS) * SECONDS_PER_DAY;

export function periodOf(rotation: Rotation, now: number): Period {
  const day = utcDay(now), week = utcWeek(day);
  switch (rotation) {
    case "daily": return { key: day, endsAt: (day + 1) * SECONDS_PER_DAY };
    case "weekly": return { key: week, endsAt: weekStart(week + 1) };
    case "fortnight": { const f = Math.floor(week / WEEKS_PER_FORTNIGHT); return { key: f, endsAt: weekStart((f + 1) * WEEKS_PER_FORTNIGHT) }; }
    case "once": case "none": return { key: 0, endsAt: null };
  }
}

export function pageOf(data: OffersData, pageId: string): OfferPageDef | null { return data.pages.find((p) => p.id === pageId) ?? null; }

export function offerOf(data: OffersData, offerId: string): OfferDef | null {
  for (const p of data.pages) { const o = p.offers.find((x) => x.id === offerId); if (o) return o; }
  return null;
}

/** The day's Daily Deals theme for this player, rolled from (player, UTC day) (Change may swap it: dealsState.ts). */
export function dailyThemeFor(data: OffersData, playerId: string, day: number): string {
  const d = pageOf(data, DAILY_PAGE)?.daily;
  if (!d || d.themes.length === 0) return "";
  return d.themes[new Pcg32(metaSeed(playerId, "dailyTheme", day), THEME_STREAM).nextInt(d.themes.length)].id;
}

/** A Daily Deals tier's price and items in a theme (tier 0..2), or Buy All (tier = "all": the three tiers' items). */
export function dailyDeal(d: DailyDealsDef, themeId: string, tier: number | "all"): { price: OfferPrice; items: OfferItem[] } | null {
  const theme = d.themes.find((t) => t.id === themeId);
  if (!theme) return null;
  const tierItems = (n: number): OfferItem[] => [{ kind: "gems", amount: d.tiers[n].gems, count: 1 }, ...theme.tiers[n]];
  if (tier === "all") return { price: d.buyAll, items: d.tiers.flatMap((_, n) => tierItems(n)) };
  return d.tiers[tier] ? { price: d.tiers[tier].price, items: tierItems(tier) } : null;
}

/** An offer as shown and as granted: everything a purchase pays, fixed when the player taps the price. */
export interface ResolvedOffer {
  /** The offer id (daily.1..3 / daily.all for Daily Deals). */
  readonly offerId: string;
  readonly pageId: string;
  readonly price: OfferPrice;
  /** Fixed items + the filled picks + the chosen prize (Daily Deals: the theme's). */
  readonly items: readonly OfferItem[];
  /** The slot index chosen for each gear item with slot 'pick', one per unit of its count, in item order. */
  readonly gearSlots: readonly number[];
  /** The rotation period the purchase counts in. */
  readonly periodKey: number;
  /** Every pick, prize and gear slot is chosen (a purchase needs it). */
  readonly complete: boolean;
  /** Holds a random item (odds shown; hidden in noRandomStorefronts). */
  readonly random: boolean;
}

/** How many gear slot choices an item list needs ('pick' gear, one per unit). */
export function gearPicksNeeded(items: readonly OfferItem[]): number {
  return items.reduce((n, it) => n + (it.kind === "gear" && it.slot === "pick" ? it.count : 0), 0);
}

/**
 * Resolves a catalog offer with the player's choices: `picks[i]` the choice index filling pick slot i, `prize` the
 * prize index, `gearSlots` the gear slot per 'pick' gear unit. A missing choice leaves `complete` false.
 */
export function resolveOffer(def: OfferDef, rotation: Rotation, now: number, picks: readonly number[], prize: number, gearSlots: readonly number[], gearSlotCount: number): ResolvedOffer {
  const chosen: OfferItem[] = [];
  let complete = true;
  for (let i = 0; i < def.picks; i++) {
    const c = def.choices[picks[i] ?? -1];
    if (c) chosen.push(c); else complete = false;
  }
  if (def.prizes.length > 0) { const p = def.prizes[prize]; if (p) chosen.push(p); else complete = false; }
  const items = [...def.items, ...chosen];
  const need = gearPicksNeeded(items);
  const slots = gearSlots.slice(0, need);
  if (slots.length < need || slots.some((s) => !Number.isInteger(s) || s < 0 || s >= gearSlotCount)) complete = false;
  return { offerId: def.id, pageId: def.pageId, price: def.price, items, gearSlots: slots, periodKey: periodOf(rotation, now).key, complete, random: hasRandomItem(items) };
}

/** Where a purchase's items go and what seeds its rolls (like events.ts RewardTarget). */
export interface DealTarget {
  readonly profile: PlayerProfile;
  readonly wallet: Wallet;
  readonly playerId: string;
  /** The player's highest cleared level (at least 1): gold, chests and gear are sized for it. */
  readonly level: number;
  readonly at: number;
  /** A stable reference (the store transaction id, or `track:<week>:<tokens>`, `vip:<day>`): the seed and the ledger ref. */
  readonly ref: string;
  /** Whether the player owns a skin already (then its refund gems are paid instead). */
  ownsSkin(skinId: string): boolean;
}

/** What a grant paid. Chests are returned unbanked: the caller banks each (app LevelCatalog.bankChest) and binds its gear. */
export interface GrantedItems {
  readonly gems: number;
  readonly gold: number;
  readonly speedUps: readonly number[];
  readonly chests: readonly ChestResult[];
  /** Gear items already added to the profile (bound: bought with money, never resold for Marks). */
  readonly gear: readonly GearItem[];
  /** Skins to unlock (the app's Cosmetics). */
  readonly skins: readonly string[];
}

/**
 * Pays items into the profile (gold, speed-ups, gear) and the wallet (gems), and rolls the chests for the caller
 * to bank. Deterministic from (playerId, ref): a re-delivered transaction would roll the same, but the caller grants a
 * transaction once (dealsState.ts `granted`).
 */
export function grantItems(balance: BalanceData, t: DealTarget, items: readonly OfferItem[], gearSlots: readonly number[]): GrantedItems {
  const level = Math.max(1, t.level);
  let gold = 0;
  const speedUps: number[] = [], chests: ChestResult[] = [], gear: GearItem[] = [], skins: string[] = [];
  let gems = 0, roll = 0, pickCursor = 0;
  const seed = () => metaSeed(t.playerId, `deal:${t.ref}`, roll++);
  const rng = new Pcg32(metaSeed(t.playerId, `deal:${t.ref}`, -1), GEAR_STREAM);
  for (const it of items) {
    switch (it.kind) {
      case "gems": gems += it.amount; break;
      case "supplies": gold += chestShare(balance, level, it.chests); break;
      case "speedUp": for (let n = 0; n < it.count; n++) speedUps.push(it.minutes); break;
      case "chest": for (let n = 0; n < it.count; n++) chests.push(chestOfRarity(balance, level, it.rarity, seed())); break;
      case "luckyChest": for (let n = 0; n < it.count; n++) chests.push(chestOfRarity(balance, level, luckyRarity(balance.offers, it.table, seed()), seed())); break;
      case "gear":
        for (let n = 0; n < it.count; n++) {
          const fit = it.slot === "pick" ? { slot: gearSlots[pickCursor++] ?? 0, maxLevelReq: level } : null;
          gear.push(t.profile.receiveGear(generateGear(balance, 0, level, it.rarity, rng, fit), { bound: true }));
        }
        break;
      case "skin":
        if (t.ownsSkin(it.skinId)) gems += skinRefund(balance, it.skinId); else skins.push(it.skinId);
        break;
    }
  }
  t.profile.addGold(gold);
  for (const m of speedUps) t.profile.addSpeedUp(m);
  if (gems > 0) t.wallet.grantGems(gems, t.ref.startsWith(TRACK_REF) ? "dealTrack" : "deal", t.at, t.ref);
  return { gems, gold, speedUps, chests, gear, skins };
}

/** A lucky chest's rarity, rolled from its percent weights. */
export function luckyRarity(data: OffersData, table: string, seed: bigint): Rarity {
  const w = data.luckyChests.get(table)?.weights ?? [];
  let r = new Pcg32(seed, LUCKY_STREAM).nextInt(PERCENT);
  for (let i = 0; i < w.length; i++) { r -= w[i]; if (r < 0) return i as Rarity; }
  return Rarity.Common;
}

/** Gems paid instead of a skin already owned (offers.json skinRefundGems at the skin's band). */
export function skinRefund(balance: BalanceData, skinId: string): number {
  const s = balance.commerce.shop.skins.list.find((k) => k.id === skinId);
  // a season skin has no band: its gem-equivalent is seasons.json repeatSkinGems (what its tier pays when owned)
  if (s?.unlock.by === "season") return balance.seasons.repeatSkinGems;
  const band = s && "band" in s.unlock ? s.unlock.band : "";
  return balance.offers.skinRefundGems.get(band) ?? 0;
}
